import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { JellyfinItem } from "../api/jellyfin";
import { NativePreferences, savePreferences } from "../storage/preferences";

type MediaSource = NonNullable<JellyfinItem["MediaSources"]>[number];

type PlayerTrackContext = {
  source?: MediaSource;
  preferences: NativePreferences;
  preferencesReady: boolean;
  setPreferences: Dispatch<SetStateAction<NativePreferences>>;
  prepareForTrackChange: (onlyIfDecoderReady: boolean) => void;
};

/** Owns Jellyfin stream IDs, native ordinals, defaults, and remembered intent. */
export function usePlayerTracks(context: PlayerTrackContext) {
  const [selectedAudioIndex, setSelectedAudioIndex] = useState<number>();
  const [selectedSubtitleIndex, setSelectedSubtitleIndex] = useState<number | null>(null);
  const initializedSourceRef = useRef("");
  const contextRef = useRef(context);
  contextRef.current = context;

  const streams = context.source?.MediaStreams ?? [];
  const audioStreams = useMemo(
    () => streams.filter((entry) => entry.Type === "Audio"),
    [streams],
  );
  const subtitleStreams = useMemo(
    () => streams.filter((entry) => entry.Type === "Subtitle"),
    [streams],
  );

  const preferredAudio = context.preferences.keepPreferredTracks && context.preferences.audioLanguage
    ? audioStreams.find((entry) => entry.Language?.toLowerCase() === context.preferences.audioLanguage.toLowerCase())
    : undefined;
  const defaultAudio = context.preferences.playDefaultAudio
    ? audioStreams.find((entry) => entry.IsDefault)
    : undefined;
  const fallbackAudioIndex = (preferredAudio ?? defaultAudio ?? audioStreams[0])?.Index;
  const tracksBelongToSource =
    Boolean(context.source?.Id) &&
    initializedSourceRef.current === context.source?.Id;
  const effectiveAudioIndex = tracksBelongToSource &&
    audioStreams.some((entry) => entry.Index === selectedAudioIndex)
    ? selectedAudioIndex
    : fallbackAudioIndex;

  const preferredSubtitle = context.preferences.keepPreferredTracks && context.preferences.subtitleLanguage
    ? subtitleStreams.find((entry) => entry.Language?.toLowerCase() === context.preferences.subtitleLanguage.toLowerCase())
    : undefined;
  const forcedSubtitle = subtitleStreams.find((entry) => entry.IsForced);
  const defaultSubtitle = subtitleStreams.find((entry) => entry.IsDefault);
  // Prefer text-based subtitles (SRT/ASS/VTT) over bitmap (PGS/SUP)
  // since the client renders text subtitles natively in the browser.
  const textSubtitle = subtitleStreams.find(
    (entry) => (entry.Codec ?? "").toLowerCase() !== "pgssub" && (entry.Codec ?? "").toLowerCase() !== "pgs" && (entry.Codec ?? "").toLowerCase() !== "sup" && (entry.Codec ?? "").toLowerCase() !== "dvdsub" && (entry.Codec ?? "").toLowerCase() !== "dvbsub"
  );
  const fallbackSubtitleIndex = context.preferences.subtitleMode === "None"
    ? null
    : (context.preferences.subtitleMode === "OnlyForced"
        ? forcedSubtitle
        : preferredSubtitle ?? (
            forcedSubtitle && (forcedSubtitle.Codec ?? "").toLowerCase() !== "pgssub" && (forcedSubtitle.Codec ?? "").toLowerCase() !== "pgs" && (forcedSubtitle.Codec ?? "").toLowerCase() !== "sup" && (forcedSubtitle.Codec ?? "").toLowerCase() !== "dvdsub" && (forcedSubtitle.Codec ?? "").toLowerCase() !== "dvbsub"
              ? forcedSubtitle
              : textSubtitle ?? forcedSubtitle ?? (
                  context.preferences.subtitleMode === "Always"
                    ? defaultSubtitle ?? subtitleStreams[0]
                    : defaultSubtitle
                )
          ))?.Index ?? null;
  const effectiveSubtitleIndex = !tracksBelongToSource
    ? fallbackSubtitleIndex
    : selectedSubtitleIndex === null
    ? null
    : subtitleStreams.some((entry) => entry.Index === selectedSubtitleIndex)
      ? selectedSubtitleIndex
      : fallbackSubtitleIndex;

  useEffect(() => {
    const { source, preferences, preferencesReady, prepareForTrackChange } = contextRef.current;
    if (
      !preferencesReady ||
      !source?.Id ||
      initializedSourceRef.current === source.Id ||
      audioStreams.length === 0
    ) return;
    initializedSourceRef.current = source.Id;
    prepareForTrackChange(true);

    const preferredAudio = preferences.keepPreferredTracks && preferences.audioLanguage
      ? audioStreams.find((entry) => entry.Language?.toLowerCase() === preferences.audioLanguage.toLowerCase())
      : undefined;
    const defaultAudio = preferences.playDefaultAudio
      ? audioStreams.find((entry) => entry.IsDefault)
      : undefined;
    setSelectedAudioIndex((preferredAudio ?? defaultAudio)?.Index);

    if (preferences.subtitleMode === "None") {
      setSelectedSubtitleIndex(null);
      return;
    }
    const preferredSubtitle = preferences.keepPreferredTracks && preferences.subtitleLanguage
      ? subtitleStreams.find((entry) => entry.Language?.toLowerCase() === preferences.subtitleLanguage.toLowerCase())
      : undefined;
    const forcedSubtitle = subtitleStreams.find((entry) => entry.IsForced);
    const defaultSubtitle = subtitleStreams.find((entry) => entry.IsDefault);
    const textSubtitle = subtitleStreams.find(
      (entry) => (entry.Codec ?? "").toLowerCase() !== "pgssub" && (entry.Codec ?? "").toLowerCase() !== "pgs" && (entry.Codec ?? "").toLowerCase() !== "sup" && (entry.Codec ?? "").toLowerCase() !== "dvdsub" && (entry.Codec ?? "").toLowerCase() !== "dvbsub"
    );
    const selectedSubtitle = preferences.subtitleMode === "OnlyForced"
      ? forcedSubtitle
      : preferredSubtitle ?? (
          // Prefer text-based subtitles (SRT/ASS/VTT) over bitmap (PGS).
          // The browser and mpv render text subtitles natively; bitmap
          // formats need JASSUB/libbitsub which may not always be available.
          forcedSubtitle && (forcedSubtitle.Codec ?? "").toLowerCase() !== "pgssub" && (forcedSubtitle.Codec ?? "").toLowerCase() !== "pgs" && (forcedSubtitle.Codec ?? "").toLowerCase() !== "sup" && (forcedSubtitle.Codec ?? "").toLowerCase() !== "dvdsub" && (forcedSubtitle.Codec ?? "").toLowerCase() !== "dvbsub"
            ? forcedSubtitle
            : textSubtitle ?? forcedSubtitle ?? (
                preferences.subtitleMode === "Always"
                  ? defaultSubtitle ?? subtitleStreams[0]
                  : defaultSubtitle
              )
        );
    setSelectedSubtitleIndex(selectedSubtitle?.Index ?? null);
  }, [audioStreams, context.preferences, context.preferencesReady, context.source?.Id, subtitleStreams]);

  const selectAudioTrack = useCallback((index: number) => {
    const current = contextRef.current;
    current.prepareForTrackChange(false);
    setSelectedAudioIndex(index);
    const language = audioStreams.find((entry) => entry.Index === index)?.Language;
    if (!current.preferences.rememberAudioSelections || !language) return;
    current.setPreferences((preferences) => {
      const next = { ...preferences, audioLanguage: language };
      void savePreferences(next);
      return next;
    });
  }, [audioStreams]);

  const selectSubtitleTrack = useCallback((index: number | null) => {
    const current = contextRef.current;
    // Subtitle track changes use the in-place text track API on web
    // (PlayerVideoSurface.web.tsx toggles TextTrack.mode).  Only audio
    // changes trigger a source reload (Remux needs a new transcode).
    // If the engine doesn't support in-place subtitle switching it will
    // ignore the index until the next source mount, which is acceptable.
    setSelectedSubtitleIndex(index);
    if (!current.preferences.rememberSubtitleSelections) return;
    const language = index === null
      ? undefined
      : subtitleStreams.find((entry) => entry.Index === index)?.Language;
    current.setPreferences((preferences) => {
      const next = index === null
        ? { ...preferences, subtitleMode: "None" as const }
        : {
            ...preferences,
            ...(language ? { subtitleLanguage: language } : {}),
            subtitleMode: preferences.subtitleMode === "None"
              ? "Always" as const
              : preferences.subtitleMode,
          };
      void savePreferences(next);
      return next;
    });
  }, [subtitleStreams]);

  const resetTracks = useCallback(() => {
    initializedSourceRef.current = "";
    setSelectedAudioIndex(undefined);
    setSelectedSubtitleIndex(null);
  }, []);

  const audioTrackIndex = effectiveAudioIndex === undefined
    ? undefined
    : audioStreams.findIndex((entry) => entry.Index === effectiveAudioIndex);
  const subtitleTrackIndex = effectiveSubtitleIndex === null
    ? null
    : effectiveSubtitleIndex === undefined
      ? undefined
      : subtitleStreams.findIndex((entry) => entry.Index === effectiveSubtitleIndex);

  return {
    streams,
    audioStreams,
    subtitleStreams,
    selectedAudioIndex: effectiveAudioIndex,
    selectedSubtitleIndex: effectiveSubtitleIndex,
    audioTrackIndex: audioTrackIndex !== undefined && audioTrackIndex >= 0 ? audioTrackIndex : undefined,
    subtitleTrackIndex: subtitleTrackIndex !== undefined && subtitleTrackIndex !== null && subtitleTrackIndex >= 0
      ? subtitleTrackIndex
      : subtitleTrackIndex === null ? null : undefined,
    selectAudioTrack,
    selectSubtitleTrack,
    resetTracks,
  };
}
